import { useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import { CategoryTabs } from "./components/CategoryTabs";
import { BookmarkForm } from "./components/BookmarkForm";
import { BookmarkGrid } from "./components/BookmarkGrid";
import { DashboardPanel } from "./components/DashboardPanel";
import { SkyBackground } from "./components/SkyBackground";
import { TimeScrubber } from "./components/TimeScrubber";
import { TopBar } from "./components/TopBar";
import { Eye } from "lucide-react";
import { useBookmarks } from "./hooks/useBookmarks";
import type { Bookmark, BookmarkCategory } from "./types";
import { makeSkyDate, toDateInputValue } from "./utils/sky";
import "./styles/app.css";

const readTokyoParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(date);
  return {
    hour: Number(parts.find((part) => part.type === "hour")?.value ?? 21),
    minute: Number(parts.find((part) => part.type === "minute")?.value ?? 0)
  };
};

const initialSliderMinutes = () => {
  const { hour, minute } = readTokyoParts(new Date());
  if (hour >= 18) return (hour - 18) * 60 + minute;
  if (hour < 6) return (hour + 6) * 60 + minute;
  return 180;
};

const initialSkyDateValue = () => {
  const now = new Date();
  const { hour } = readTokyoParts(now);
  if (hour >= 6) return toDateInputValue(now);
  return toDateInputValue(new Date(now.getTime() - 86400000));
};

const defaultSkyAzimuth = 180;
const skyDragDegreesPerPixel = 0.22;
const normalizeSkyAzimuth = (value: number) => ((value % 360) + 360) % 360;

type SkyDragState = {
  pointerId: number;
  startX: number;
  startAzimuth: number;
  moved: boolean;
};

export default function App() {
  const { bookmarks, saveBookmark, deleteBookmark, resetBookmarks, reorderBookmarks } = useBookmarks();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<BookmarkCategory | "All">("All");
  const [editing, setEditing] = useState<Bookmark | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [dateValue, setDateValue] = useState(initialSkyDateValue);
  const [sliderMinutes, setSliderMinutes] = useState(initialSliderMinutes);
  const [now, setNow] = useState(() => new Date());
  const [chromeHidden, setChromeHidden] = useState(false);
  const [skyCenterAzimuth, setSkyCenterAzimuth] = useState(defaultSkyAzimuth);
  const skyDrag = useRef<SkyDragState | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const clockDate = useMemo(() => new Date(now.getTime() + 12 * 60 * 1000), [now]);
  const skyDate = useMemo(() => makeSkyDate(dateValue, sliderMinutes), [dateValue, sliderMinutes]);

  const filteredBookmarks = useMemo(() => {
    const search = query.trim().toLowerCase();
    return bookmarks.filter((bookmark) => {
      const categoryMatch = activeCategory === "All" || bookmark.category === activeCategory;
      const searchMatch =
        search.length === 0 ||
        [bookmark.label, bookmark.url, bookmark.description, bookmark.category]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(search));
      return categoryMatch && searchMatch;
    });
  }, [activeCategory, bookmarks, query]);

  const openAdd = () => {
    setChromeHidden(false);
    setEditing(null);
    setFormKey((value) => value + 1);
    setDrawerOpen(true);
  };

  const openEdit = (bookmark: Bookmark) => {
    setChromeHidden(false);
    setEditing(bookmark);
    setFormKey((value) => value + 1);
    setDrawerOpen(true);
  };

  const handleShellClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (chromeHidden) return;
    const target = event.target as HTMLElement;
    if (
      target.closest(
        ".topbar, .workspace, .reset-link, .time-scrubber, .drawer-backdrop, .focus-restore"
      )
    ) {
      return;
    }
    setChromeHidden(true);
  };

  const handleSkyPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!chromeHidden) return;
    event.preventDefault();
    skyDrag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startAzimuth: skyCenterAzimuth,
      moved: false
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleSkyPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = skyDrag.current;
    if (!chromeHidden || !drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    if (Math.abs(deltaX) > 3) drag.moved = true;
    setSkyCenterAzimuth(normalizeSkyAzimuth(drag.startAzimuth - deltaX * skyDragDegreesPerPixel));
    event.preventDefault();
  };

  const handleSkyPointerEnd = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = skyDrag.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    skyDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (drag.moved) event.preventDefault();
  };

  return (
    <main
      className={`app-shell ${chromeHidden ? "chrome-hidden" : ""}`}
      data-testid="overlay-root"
      data-sky-azimuth={skyCenterAzimuth.toFixed(1)}
      onClickCapture={handleShellClickCapture}
    >
      <SkyBackground skyDate={skyDate} centerAzimuth={skyCenterAzimuth} />
      <button
        type="button"
        className="sky-focus-hotspot"
        onClick={() => {
          if (!chromeHidden) setChromeHidden(true);
        }}
        onPointerDown={handleSkyPointerDown}
        onPointerMove={handleSkyPointerMove}
        onPointerUp={handleSkyPointerEnd}
        onPointerCancel={handleSkyPointerEnd}
        aria-label={chromeHidden ? "星空の方角をドラッグ" : "背景だけ表示"}
        data-testid="sky-focus-hotspot"
      />
      <TopBar
        query={query}
        onQueryChange={setQuery}
        onAdd={openAdd}
        chromeHidden={chromeHidden}
        onToggleChrome={() => setChromeHidden((value) => !value)}
      />

      <section className="hero">
        <span className="hero-mark" aria-hidden="true" />
        <h1>こんばんは、 masatomo.</h1>
      </section>

      <div className="workspace">
        <div className="bookmark-region">
          <CategoryTabs active={activeCategory} onChange={setActiveCategory} />
          <BookmarkGrid bookmarks={filteredBookmarks} onEdit={openEdit} onReorder={reorderBookmarks} />
        </div>
        <DashboardPanel clockDate={clockDate} skyDate={skyDate} />
      </div>

      <button type="button" className="reset-link" onClick={resetBookmarks}>
        初期ブックマークに戻す
      </button>

      <TimeScrubber
        dateValue={dateValue}
        sliderMinutes={sliderMinutes}
        skyDate={skyDate}
        onDateChange={setDateValue}
        onSliderChange={setSliderMinutes}
        onAdd={openAdd}
      />

      {chromeHidden && (
        <button
          type="button"
          className="focus-restore"
          onClick={() => setChromeHidden(false)}
          aria-label="アイコン類を表示"
          data-testid="overlay-restore"
        >
          <Eye size={16} />
          表示
        </button>
      )}

      <BookmarkForm
        key={formKey}
        open={drawerOpen}
        bookmark={editing}
        onClose={() => setDrawerOpen(false)}
        onSave={saveBookmark}
        onDelete={deleteBookmark}
      />
    </main>
  );
}
